/* eslint-disable max-lines */
import { logger } from '@adornis/base/logging.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import '@adornis/datetime/x-timerange-picker.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import '@adornis/dragdrop/x-drag-drop.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import '@adornis/forms/x-input.js';
import '@adornis/forms/x-toggle-button.js';
import { Price } from '@adornis/payments/db/prices.js';
import '@adornis/popover/x-dropdown-multiselection.js';
import '@adornis/popover/x-dropdown-selection.js';
import '@adornis/popover/x-dropdown.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { NO_CHANGE } from '@adornis/router/no-change.js';
import '@ils/locations/client/ils-location-selection.js';
import type { Location } from '@ils/locations/db/location.js';
import { subscribeAllOsces } from '@ils/osces/api/osce.js';
import '@ils/osces/client/ils-osce-type.js';
import type { OSCE } from '@ils/osces/db/osce.js';
import { checkForAnswersInSlots } from '@ils/slots/api/slot.js';
import { ExamType, isTextOsceCategory, prettyOsceCategory, UserRole } from '@ils/static/enums.js';
import Chart from 'chart.js/auto';
import 'chartjs-adapter-luxon';
import { html, nothing } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { state } from 'lit/decorators/state.js';
import { repeat } from 'lit/directives/repeat.js';
import { DateTime } from 'luxon';
import { combineLatest, from, type Observable, of } from 'rxjs';
import { delay, distinctUntilKeyChanged, filter, map, switchMap } from 'rxjs/operators';
import { ExamDay, type OsceTesterTuple } from '../../db/examdays.js';
import { ILSUser } from '../../db/ils-user.js';
import '../components/ils-editor-exam-osce-selection.js';
import '../components/ils-user-multi-selection.js';

const examDaySelector: BaseQLSelectionSet<ExamDay> = {
  _id: 1,
  _class: 1,
  examType: 1,
  start: 1,
  end: 1,
  locationID: 1,
  breakAfterRounds: 1,
  breakInMinutes: 1,
  slotPause: 1,
  blockPause: 1,
  testers: 1,
  candidateIDs: 1,
  maxCandidates: 1,
  slotIDs: 1,
  examCenter: 1,
  osceTesterTuples: {
    _class: 1,
    osceID: 1,
    testerIDs: 1,
    osceGroupID: 1,
    osceGroupIndex: 1,
    osce: {
      _id: 1,
      _class: 1,
      title: 1,
      taskType: 1,
      osceType: 1,
      enableVideo: 1,
      conversationType: 1,
      durationInMinutes: 1,
    },
  },
  fallbackTesters: 1,
  name: 1,
  prices: Price.allFields,
  published: 1,
  isPublishedForTesters: 1,
  participatedOSCEs: 1,
  ratingType: 1,
};

@customElement('ils-editor-exam')
export class ILSEditorExam extends ChemistryLitElement {
  override styles() {
    return [
      {
        ':host': {
          flex: '1',
          display: 'flex',
          overflow: 'auto',
        },
      },
    ];
  }

  @state() private dragging = false;

  @state() private readonly selectedTab = new RXController(this, '0');

  @property({ attribute: 'examday-id' }) examdayID = new RXController(this, '');

  @state() private readonly examDay = new RXController<Observable<ExamDay>, ExamDay>(
    this,
    this.examdayID.observable.pipe(
      switchMap(examID => (examID ? subscribeByID(ExamDay, examID)(examDaySelector) : of(undefined))),
      filter(Boolean),
      switchMap(examDay => {
        examDay.generateTesterArrays();
        return of(examDay);
      }),
      switchMap(examDay => examDay.whenChanging),
    ),
  );

  @state() private readonly examDaySubscription = this.examDay.observable.pipe(delay(100)).subscribe(obs => {
    this.drawTimeline();
  });

  private readonly allOsces = this.examDay.observable.pipe(
    filter(Boolean),
    distinctUntilKeyChanged('_id'),
    switchMap(examDay =>
      subscribeAllOsces(
        examDay.examType ?? ExamType.FAMED,
        false,
      )({
        _id: 1,
        _class: 1,
        title: 1,
        taskType: 1,
        osceType: 1,
        conversationType: 1,
        enableVideo: 1,
        durationInMinutes: 1,
        archivedAt: 1,
      }),
    ),
    map(osces => osces.filter(osce => osce.osceType)),
  );

  @state() private readonly osces = new RXController(this, this.allOsces);

  @state() private readonly groupedOsces = new RXController(
    this,

    this.allOsces.pipe(
      map(osces =>
        osces.reduce((sum, item: OSCE) => {
          if (!item.osceType) return sum;
          sum[item.osceType] ||= [];

          sum[item.osceType].push(item);

          return sum;
        }, {}),
      ),
    ),
  );

  @state() private readonly actorFilter = new RXController(this, '');

  private readonly testers = from(
    ILSUser.getUsersByRoles([UserRole.TESTER])({
      _id: 1,
      _class: 1,
      username: 1,
      ecommerce: { _class: 1, firstName: 1, lastName: 1 },
      assignedOSCETypes: 1,
    }) as unknown as Promise<ILSUser[]>,
  );

  @state() private readonly allTesters = new RXController(this, this.testers);

  @state() private readonly _viableTesters = new RXController(
    this,
    combineLatest([
      this.testers,
      this.examDay.observable,
      this.selectedTab.observable,
      this.actorFilter.observable,
      this.osces.observable,
    ]).pipe(
      map(([testers, examDay, selectedTab, actorFilter, osces]) => {
        if (selectedTab === '0') {
          return testers
            .filter(tester =>
              examDay.osceTesterTuples.some(
                tuple => tuple.osce?.osceType && tester.assignedOSCETypes?.includes(tuple.osce.osceType),
              ),
            )
            .filter(tester => tester.ecommerce?.fullName.toLowerCase().includes(actorFilter));
        }

        const osce = (osces ?? []).find(osce => osce._id === selectedTab);

        return testers
          .filter(tester => !osce || tester.assignedOSCETypes?.includes(osce.osceType))
          .filter(tester => tester.ecommerce?.fullName.toLowerCase().includes(actorFilter));
      }),
      map(testers => {
        return testers.sort((a, b) => {
          if (!a?.ecommerce?.fullName) return -1;
          if (!b?.ecommerce?.fullName) return 1;
          return a.ecommerce.fullName.localeCompare(b.ecommerce.fullName);
        });
      }),
    ),
  );

  @state() private readonly hasAnswers = new RXController(
    this,
    this.examDay.observable.pipe(
      filter(Boolean),
      switchMap(examDay => checkForAnswersInSlots(examDay.slotIDs)()),
    ),
  );

  @state() private timelineChart: Maybe<Chart>;

  @state() private readonly _form = new FormController(this, this.examDay.observable);

  @query('.timeline-chart')
  timelineChartCanvas: Maybe<HTMLCanvasElement>;

  private readonly dropzoneStyle = {
    transition: 'all .2s',
    color: this.colors.neutral.shade(200),
    border: `2px dashed ${this.colors.neutral.shade(200)}`,
    padding: '12px',
    borderRadius: this.sizes.borderRadius,
    userSelect: 'none',
    width: '100%',
  };

  private readonly draggableStyle = css({
    background: this.colors.neutral.shade(100),
    borderRadius: this.sizes.borderRadius,
    boxShadow: '1px 3px 4px 0px rgba(0,0,0,0.3) !important',
    userSelect: 'none',
    minHeight: '36px',
    padding: '8px',
  });

  private readonly dropzoneStyleDragging = {
    ...this.dropzoneStyle,
    transform: 'scale(1.05)',
    color: this.colors.accent,
    border: `2px dashed ${this.colors.accent}`,
  };

  generateTimelineEntry(date, type) {
    return {
      label: type,
      data: [
        {
          x: new Date(date),
          y: 1,
        },
      ],
      backgroundColor: type === 'round' ? this.colors.accent.string : this.colors.neutral.shade(50).string,
    };
  }

  drawTimeline() {
    if (!this.examDay.value?.osceTesterTuples.length) return;

    const timelineData = this.examDay.value.calculateRoundsWithinExamDay().timeline;

    const ctx = this.timelineChartCanvas?.getContext('2d');

    if (!ctx) return;

    this.timelineChart?.destroy();

    this.timelineChart = new Chart(ctx, {
      type: 'bar',
      data: {
        datasets: [
          ...timelineData.flatMap(timeChunk => {
            return [
              this.generateTimelineEntry(timeChunk.start, timeChunk.type),
              this.generateTimelineEntry(timeChunk.end, timeChunk.type),
            ];
          }),
        ],
      },
      options: {
        animation: {
          duration: 0,
        },
        responsive: true,
        indexAxis: 'y',
        plugins: {
          tooltip: {
            enabled: false,
          },
          legend: {
            display: false,
          },
          title: {
            display: false,
          },
        },
        scales: {
          y: {
            stacked: true,
          },
          x: {
            type: 'time',
            time: {
              tooltipFormat: 'DD',
            },
            min: new Date(this.examDay.value.start).toISOString(),
            max: new Date(this.examDay.value.end).toISOString(),
          },
        },
      },
    });
  }

  override connectedCallback(): void {
    super.connectedCallback();

    this.drawTimeline();
  }

  override disconnectedCallback(): void {
    this.examDaySubscription.unsubscribe();
  }

  override render() {
    const maxWidth = 1000 + 32 + 'px';
    if (this.examDay.isLoading) return html` <x-flex center crossaxis-center> <ils-loader></ils-loader> </x-flex> `;
    if (!this.examDay.value) return '';

    return html`
      <x-flex padding="md" space="lg" ${css({ width: '100%', maxWidth })}>
        <x-flex horizontal crossaxis-center space-between>
          <x-flex horizontal crossaxis-center space="sm">
            <x-button
              mode="icon"
              icon="arrow_back"
              @click=${() => {
                try {
                  window.history.back();
                } catch {
                  goTo({ path: '/start/editor-exam', searchParams: NO_CHANGE });
                }
              }}
            ></x-button>
            <x-h1> ${this.examDay.value.examType} ${this.examDay.value.name} </x-h1>
          </x-flex>
          <x-flex horizontal space="md">
            <x-button
              mode="outline"
              ?disabled=${this.hasAnswers.value}
              @click="${() => this.deleteExamDay(this.examDay.value!)}"
            >
              Prüfungstag löschen
            </x-button>
            <x-button
              primary
              @click=${async () => {
                try {
                  await this._saveExamday();
                } catch (e) {
                  const error = e as Error;
                  logger.error(e);
                  void XDialog.alert(error.message);
                }
              }}
            >
              Änderungen speichern
            </x-button>
          </x-flex>
        </x-flex>

        <x-flex space="lg" ${css({ paddingBottom: this.spacing.xxl })}>
          ${this.overviewGrid()} ${this.chosenOSCEsCard(this.examDay.value)}
        </x-flex>
      </x-flex>
    `;
  }

  private overviewGrid() {
    return html`
      <x-flex space="md">
        <x-h2>Basisdaten</x-h2>
        <x-card ${css({ width: '100%' })}>
          <x-grid columns="1fr 1fr" ${css({ gap: this.spacing.md })}>
            <x-flex space="sm">
              <x-h3>Preisgestaltung der Prüfung</x-h3>
              ${this.pricePartial()}
            </x-flex>

            <x-flex space="sm">
              <x-h3> Sichtbarkeit </x-h3>
              <x-toggle-button label="Prüfungstag veröffentlichen" ${this._form.field('published')}></x-toggle-button>
              <x-toggle-button
                label="Prüfungstag für Schauspieler veröffentlichen"
                ${this._form.field('isPublishedForTesters')}
              ></x-toggle-button>
            </x-flex>
            <x-h3 ${css({ gridColumn: 'span 2' })}> Standort </x-h3>
            <ils-location-selection
              placeholder="Prüfungsstandort"
              ${this._form.field('locationID')}
              @entity-picked=${(e: ValueEvent<Location>) => {
                if (!this._form.document) return;
                this._form.document.location = e.detail.value;
              }}
            ></ils-location-selection>
            <ils-user-multi-selection
              placeholder="Vor Ort Betreuung"
              .selectedLocation=${this._form.document?.locationID}
              ${this._form.field('examCenter')}
            ></ils-user-multi-selection>

            <x-flex space="sm" ${css({ width: '100%', gridColumn: 'span 2' })}>
              <x-h3>Zeitliche Rahmenbedingungen</x-h3>
              ${this.datePickerPartial()}
            </x-flex>
          </x-grid>
        </x-card>
      </x-flex>
    `;
  }

  private pricePartial() {
    return html`
      <x-flex space="md" ${css({ width: '100%' })}>
        <x-flex horizontal crossaxis-center space="md">
          <x-h4 ${css({ width: '100%' })}> Preis </x-h4>
          <x-input
            ${css({ width: '100%' })}
            type="number"
            min="0"
            postfix="€"
            .value=${this.examDay.value!.price / 100}
            @value-picked=${(e: ValueEvent<number>) => {
              if (!this.examDay.value) return;
              if (!this.examDay.value.prices) this.examDay.value.prices = [new Price({})];
              this.examDay.value.prices[0]!.unitAmount = +(e.detail.value * 100).toFixed(0);
            }}
          ></x-input>
        </x-flex>
      </x-flex>
    `;
  }

  private datePickerPartial() {
    const dayStart = this.examDay.value!.start;
    const dayEnd = this.examDay.value!.end;

    // todo: refactor x-input date and x-timerange-picker to single x-input datetime-local
    return html`
      <x-flex space="md">
        <x-grid space="md" columns="1fr 1fr 1fr 1fr">
          <x-flex center> <x-h4> Tag der Prüfung </x-h4> </x-flex>
          <x-input
            type="date"
            postfix="Datum"
            .value=${this.examDay.value?.start || DateTime.now()}
            .isDateBlocked=${(date: DateTime) => date < DateTime.now().minus({ days: 1 })}
            @value-picked=${(e: ValueEvent<DateTime>) => this.updateExamDayDate(e)}
          ></x-input>

          <x-flex center> <x-h4> Prüfungszeit </x-h4> </x-flex>
          <x-timerange-picker
            .value=${[dayStart, dayEnd]}
            @value-picked=${(e: ValueEvent<[DateTime, DateTime]>) => {
              this.examDay.value!.start = this.examDay.value!.start.set({
                hour: e.detail.value[0].hour,
                minute: e.detail.value[0].minute,
                second: 0,
              });
              this.examDay.value!.end = this.examDay.value!.start.set({
                hour: e.detail.value[1].hour,
                minute: e.detail.value[1].minute,
                second: 0,
              });
            }}
          ></x-timerange-picker>

          <x-flex center horizontal space="sm">
            <x-toggle-button
              .value=${!!this.examDay.value?.breakAfterRounds}
              @value-picked=${(e: ValueEvent<boolean>) => {
                if (!!this.examDay.value?.breakAfterRounds) {
                  this.examDay.value.breakAfterRounds = 0;
                } else if (this.examDay.value) {
                  this.examDay.value.breakAfterRounds = 1;
                }
              }}
            ></x-toggle-button>
            <x-h4>Mittagspause alle X Runden</x-h4>
          </x-flex>
          <x-input
            ?disabled=${!this.examDay.value?.breakAfterRounds}
            type="number"
            postfix="Runden"
            ${this._form.field('breakAfterRounds')}
          ></x-input>

          <x-flex center> <x-h4> Dauer einer Mittagspause </x-h4> </x-flex>
          <x-input type="number" min="0" postfix="min" ${this._form.field('breakInMinutes')}></x-input>

          <x-flex center> <x-h4> Pause zwischen Slots </x-h4> </x-flex>
          <x-input type="number" min="0" postfix="min" ${this._form.field('slotPause')}></x-input>

          <x-flex center> <x-h4> Wechselzeit zwischen Runden </x-h4> </x-flex>
          <x-input type="number" min="0" postfix="min" ${this._form.field('blockPause')}></x-input>
        </x-grid>
      </x-flex>

      <div class="timeline-chart-view">
        <canvas class="timeline-chart" width="900" height="80"></canvas>
      </div>
    `;
  }

  private chosenOSCEsCard(examDay: ExamDay) {
    if (!this.groupedOsces.value) return nothing;

    const selectedTabOsce = this.osces.value?.find(osce => osce._id === this.selectedTab.value);
    const isTextOsce = isTextOsceCategory(selectedTabOsce?.osceType);

    return html`
      <x-flex space="md" ${css({ minHeight: '700px' })}>
        <x-h2> Ausgewählte OSCE's </x-h2>

        <x-basic-card>
          <x-tabs .selected=${this.selectedTab.value} @selected-changed=${e => this.selectedTab.next(e.detail.value)}>
            <x-tab label="0"> Übersicht </x-tab>

            ${repeat(examDay.osceTesterTuples, tuple => {
              const warning = examDay.participatedOSCEs?.includes(tuple.osceID);
              return html`
                <x-tab label=${tuple.osceID ?? ''} ${css({ color: warning ? this.colors.tone.warning : undefined })}>
                  ${prettyOsceCategory(tuple.osce?.osceType)}
                </x-tab>
              `;
            })}
          </x-tabs>
        </x-basic-card>

        <!-- right side draggable -->
        <x-drag-drop
          class="tester-draggables"
          drag-mode=${'freeMove'}
          child-selector=${'.draggable'}
          allow-from=${'.draggable'}
          ${css({ width: '100%' })}
          @dragging-changed=${e => {
            this.dragging = e.detail.value;
          }}
        >
          <x-grid columns="1fr max-content" ${css({ gap: this.spacing.md })} ${css({ width: '100%' })}>
            <x-flex w100>
              <x-flex ${css({ width: '100%', display: this.selectedTab.value === '0' ? undefined : 'none' })}>
                ${this.overviewTab(examDay)}
              </x-flex>

              ${repeat(examDay.osceTesterTuples, tuple => this.osceTab(examDay, tuple))}
            </x-flex>

            <x-basic-card
              ${css({
                height: 'max-content',
                overflow: 'unset',
                width: '320px',
                position: 'sticky',
                top: '12px',
              })}
              space="md"
              padding="md"
            >
              <x-flex space="md">
                <x-flex horizontal crossaxis-center space-between space="sm">
                  <x-h3>${this.selectedTab.value === '0' ? 'Ersatz-Schauspieler' : 'Schauspieler'}</x-h3>
                  <x-text
                    ${css({
                      fontSize: '12px',
                      fontWeight: '600',
                      color: this.colors.neutral.shade(200),
                    })}
                  >
                    Drag&Drop möglich
                  </x-text>
                </x-flex>
              </x-flex>

              ${isTextOsce
                ? html`<x-text>Schriftliche OSCE (Keine Schauspieler nötig)</x-text>`
                : html`
                  <x-button
                    icon="manage_accounts"
                    @click=${async () => {
                      if (
                        await XDialog.confirm(
                          'Wenn du in die Nutzerverwaltung wechseln möchtest, wird dieser Prüfungstag automatisch gespeichert. Trotzdem fortfahren?',
                        )
                      ) {
                        await this._saveExamday();
                        goTo({
                          path: `/start/users`,
                          searchParams: NO_CHANGE,
                        });
                      }
                    }}>Zur Nutzerverwaltung</x-button>
                    
                    <x-input
                      icon="search"
                      clearable
                      placeholder="${this.selectedTab.value === '0' ? 'Ersatz-Schauspieler' : 'Schauspieler'} suchen"
                      type="text"
                      @value-changed=${event => {
                        this.actorFilter.next(event.detail.value.toLowerCase());
                      }}
                      .value=${this.actorFilter.value}
                      ></x-input>

                    <x-flex space="sm">
                      ${
                        this._viableTesters.value?.length
                          ? repeat(this._viableTesters.value.slice(0, 10), tester => this.draggablePartial(tester))
                          : html` <x-text>Keine zuweisbaren Schauspieler gefunden.</x-text> `
                      }
                      ${
                        (this._viableTesters.value ?? []).length > 10
                          ? html`
                              <x-text tone="subtle" align="center">
                                Für weitere Schauspieler die Suche weiter eingrenzen...
                              </x-text>
                            `
                          : nothing
                      }
                    </x-flex>
                  </x-flex>
                  `}
            </x-basic-card>
          </x-grid>
        </x-drag-drop>
      </x-flex>
    `;
  }

  private testerDraggableOsceTab(examDay: ExamDay, roundIndex, tuple: OsceTesterTuple) {
    const testerID = tuple.testerIDs[roundIndex];

    const tester = this.allTesters.value?.find(tester => tester._id === testerID);

    return html`
      <x-flex
        class=".draggable--round handle"
        ${this.draggableStyle}
        horizontal
        w100
        crossaxis-center
        space="sm"
        key=${'osce-draggable-' + testerID}
      >
        <x-text> ${tester?.ecommerce?.fullName} </x-text>
      </x-flex>

      <x-button
        mode="icon"
        icon="delete"
        tone="error"
        @click=${() => this.removeTesterFromRound(roundIndex, tuple)}
      ></x-button>
    `;
  }

  private testerDroppableOsceTab(roundIndex, tuple: OsceTesterTuple) {
    return html`
      <x-drag-drop
        allow-from=${'.draggable'}
        class="tester-dropzone"
        dropzone-accept-selector=${'.draggable'}
        roundIndex=${roundIndex}
        ${css({ width: '100%' })}
        @dropped=${e => {
          const testerID = e.detail.attributes.key.value;
          const tuple = this.examDay.value?.osceTesterTuples?.find(tuple => tuple.osceID === this.selectedTab.value);

          if (!tuple) return;

          if (!this.canDropInThisRound(roundIndex, testerID)) {
            void XSnackbar.show('Der Schauspieler wurde in dieser Runde schon zugewiesen');
            return;
          }

          tuple.testerIDs[roundIndex] = testerID;
        }}
      >
        <x-text ${this.dragging ? css(this.dropzoneStyleDragging) : css(this.dropzoneStyle)} class="tester-dropzone"
          >Schauspieler hier hereinziehen</x-text
        >
      </x-drag-drop>
    `;
  }

  private fallbackTesterDraggableOsceTab(examDay: ExamDay, roundIndex: number) {
    const fallbackTesterID = this.examDay.value?.fallbackTesters[roundIndex];

    const tester = this.allTesters.value?.find(tester => tester._id === fallbackTesterID);

    return html`
      <x-flex horizontal crossaxis-center space="sm">
        <x-flex
          class=".draggable--round handle"
          ${this.draggableStyle}
          horizontal
          w100
          crossaxis-center
          space="sm"
          key=${'osce-draggable-' + 'myname'}
        >
          <x-text>${tester?.ecommerce?.fullName} </x-text>
        </x-flex>

        <x-button
          mode="icon"
          icon="delete"
          tone="error"
          @click=${() => this.removeFallbackTesterFromRound(examDay, roundIndex)}
        ></x-button>
      </x-flex>
    `;
  }

  private fallbackTesterDroppableOsceTab(roundIndex) {
    return html`
      <x-drag-drop
        allow-from=${'.draggable'}
        class="tester-dropzone"
        dropzone-accept-selector=${'.draggable'}
        roundIndex=${roundIndex}
        ${css({ width: '100%' })}
        @dropped=${e => {
          const testerID = e.detail.attributes.key.value;

          const testersInRound = this.examDay.value?.osceTesterTuples.map(tuple => tuple.testerIDs?.[roundIndex]);

          if (testersInRound?.includes(testerID)) {
            void XSnackbar.show(
              'Schauspieler ist der gewählten Runde schon zugewiesen und kann nicht als Ersatz-Schauspieler eingesetzt werden.',
            );

            return;
          }

          this.examDay.value.fallbackTesters[roundIndex] = testerID;
        }}
      >
        <x-text ${this.dragging ? css(this.dropzoneStyleDragging) : css(this.dropzoneStyle)} class="tester-dropzone">
          Ersatz-Schauspieler für die gesamte Runde hier hereinziehen
        </x-text>
      </x-drag-drop>
    `;
  }

  private draggablePartial(tester: ILSUser) {
    return html`
      <x-flex
        class="draggable handle"
        ${this.draggableStyle}
        horizontal
        w100
        crossaxis-center
        space="sm"
        key=${tester._id}
      >
        <x-icon ${css({ fontSize: '24px', color: this.colors.neutral.shade(300) })}>drag_indicator</x-icon>
        <x-text> ${tester.ecommerce?.fullName} </x-text>
      </x-flex>
    `;
  }

  private overviewTab(examDay: ExamDay) {
    return html`
      <x-flex space="lg">
        <x-basic-card padding="md">
          <ils-editor-exam-osce-selection
            .examDay=${examDay}
            .allOSCEs=${this.osces.value}
          ></ils-editor-exam-osce-selection>
        </x-basic-card>

        ${repeat(Array(this.examDay.value?.calculateRoundsWithinExamDay().rounds), (item, index) =>
          this.roundOverviewPartial(examDay, index),
        )}
      </x-flex>
    `;
  }

  private findOsceForTuple(tuple) {
    return this.osces.value?.find(osce => osce._id === tuple.osceID);
  }

  private osceTab(examDay: ExamDay, tuple: OsceTesterTuple) {
    const isTextOsce = tuple.osce?.taskType === 'text';
    const isSelected = !!tuple.osce?.osceType && this.selectedTab.value === tuple.osceID;

    if (!tuple.osce) return nothing;
    return html`
      <x-basic-card
        padding="md"
        ${css({
          width: '100%',
          display: isSelected ? undefined : 'none',
        })}
      >
        <x-flex space="lg">
          <x-flex space="sm">
            <x-h3>OSCE: ${prettyOsceCategory(tuple.osce.osceType)}</x-h3>
            <x-flex horizontal crossaxis-center space="sm">
              <ils-osce-type .osce=${tuple.osce}></ils-osce-type> <x-text> ${tuple.osce.title} </x-text>
            </x-flex>
          </x-flex>
          ${isTextOsce
            ? nothing
            : html`
                <x-flex space="md">
                  <x-flex horizontal crossaxis-center space="sm">
                    <x-icon> theater_comedy </x-icon>
                    <x-h4> Schauspieler </x-h4>
                  </x-flex>

                  ${repeat(Array(examDay.calculateRoundsWithinExamDay().rounds), (item, roundIndex) =>
                    this.roundOSCEPartial(examDay, roundIndex, tuple),
                  )}
                </x-flex>
              `}
        </x-flex>
      </x-basic-card>
    `;
  }

  private roundOSCEPartial(examDay: ExamDay, roundIndex: number, tuple: OsceTesterTuple) {
    const counter = roundIndex + 1;

    return html`
      <x-grid columns="max-content 1fr max-content" ${css({ alignItems: 'center', gap: this.spacing.md })}>
        <x-text>${counter}. Runde</x-text>

        ${this.testerDropzonePartial(examDay, roundIndex, tuple)}
      </x-grid>
    `;
  }

  private removeTesterFromRound(roundIndex, tuple: OsceTesterTuple) {
    tuple.testerIDs[roundIndex] = null;
  }

  private removeFallbackTesterFromRound(examDay: ExamDay, roundIndex: number) {
    examDay.fallbackTesters[roundIndex] = null;
  }

  private roundOverviewPartial(examDay: ExamDay, roundIndex: number) {
    const counter = roundIndex + 1;

    return html`
      <x-basic-card padding="md">
        <x-flex space="md">
          <x-h3>${counter}. Runde</x-h3>

          <x-flex horizontal crossaxis-center space="sm">
            <x-icon>theater_comedy</x-icon>
            <x-h4>Schauspieler</x-h4>
          </x-flex>

          <x-grid columns="max-content 1fr" ${css({ gap: this.spacing.md, alignItems: 'center' })}>
            ${repeat(examDay.osceTesterTuples, tuple => {
              const osce = this.findOsceForTuple(tuple);

              if (osce?.taskType === 'text')
                return html`
                  <x-text ${css({ fontWeight: '600' })}>${prettyOsceCategory(osce?.osceType)}</x-text>
                  <x-text><i>Schriftlicher OSCE (kein Schauspieler benötigt)</i></x-text>
                `;

              return html`
                <x-text ${css({ fontWeight: '600' })}>${prettyOsceCategory(osce?.osceType)}</x-text>
                ${tuple.testerIDs?.[roundIndex]
                  ? html`
                      <x-text>
                        ${this.allTesters.value?.find(tester => tester._id === tuple.testerIDs?.[roundIndex])?.ecommerce
                          ?.fullName || tuple.testerIDs?.[roundIndex]}
                      </x-text>
                    `
                  : html`
                      <x-flex horizontal crossaxis-center space="sm">
                        <x-button
                          mode="text"
                          @click=${() => {
                            if (!tuple.osce?.osceType) return;

                            this.selectedTab.value = tuple.osceID;
                            this.selectedTab.next(this.selectedTab.value);
                          }}
                          >zuweisen</x-button
                        >
                      </x-flex>
                    `}
              `;
            })}
          </x-grid>
          ${this.fallbackTesterDropzonePartial(examDay, roundIndex)}
        </x-flex>
      </x-basic-card>
    `;
  }

  private fallbackTesterDropzonePartial(examDay: ExamDay, roundIndex: number) {
    const fallbackTester = this.examDay.value?.fallbackTesters[roundIndex];

    return fallbackTester && fallbackTester !== null
      ? this.fallbackTesterDraggableOsceTab(examDay, roundIndex)
      : this.fallbackTesterDroppableOsceTab(roundIndex);
  }

  private canDropInThisRound(roundIndex: number, testerID: string) {
    const testersInRound = this.examDay.value.osceTesterTuples.map(tuple => tuple?.testerIDs[roundIndex]);

    const alreadyInRound = testersInRound.includes(testerID);

    return !alreadyInRound;
  }

  private testerDropzonePartial(examDay: ExamDay, roundIndex: number, tuple: OsceTesterTuple) {
    if (!tuple || !tuple.testerIDs) return;

    return tuple.testerIDs[roundIndex] && tuple.testerIDs[roundIndex] !== null
      ? this.testerDraggableOsceTab(examDay, roundIndex, tuple)
      : this.testerDroppableOsceTab(roundIndex, tuple);
  }

  private updateExamDayDate(e: ValueEvent<DateTime>) {
    if (!this.examDay.value) return;
    const day = e.detail.value.day;
    const month = e.detail.value.month;
    const year = e.detail.value.year;

    this.examDay.value.start = this.examDay.value.start.set({ year, month, day });
    this.examDay.value.end = this.examDay.value.end.set({ year, month, day });

    this.examDay.value.name = `${this.examDay.value.start.toLocaleString(
      DateTime.DATE_SHORT,
    )}, ${this.examDay.value.start.toLocaleString(DateTime.TIME_24_SIMPLE)} - ${this.examDay.value.end.toLocaleString(
      DateTime.TIME_24_SIMPLE,
    )}`;
  }

  private async deleteExamDay(examDay: ExamDay) {
    if (
      await XDialog.confirm(`Möchten Sie den ExamDay "${examDay.name}" wirklich löschen?`, {
        highlightDecline: true,
      })
    ) {
      try {
        const deleted = await examDay.remove();

        if (deleted) goTo({ path: '/start/editor-exam', searchParams: NO_CHANGE });
        else void XDialog.alert(`Etwas ist beim Löschen des ExamDay's ${examDay.name} schief gelaufen!`);
      } catch (error: any) {
        void XDialog.alert(error.message);
        logger.error(error.message);
        return false;
      }
    }
  }

  private async _saveExamday() {
    if (!this.examDay.value) return;
    await this.examDay.value.save();
    void XSnackbar.show('Prüfungstag wurde gespeichert.');
  }
}
